import * as React from 'react'
import { Fragment, useState } from 'react'

import { renderers } from '@markdoc/markdoc'
import cn from 'clsx'
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'

import BlogAuthors from '@/components/BlogAuthors'
import BlogCategoryLink from '@/components/BlogCategoryLink'
import Callout from '@/components/content/Callout'
import CodeBlock from '@/components/content/CodeBlock'
import ContentImage from '@/components/content/ContentImage'
import Details from '@/components/content/Details'
import Iframe from '@/components/content/Iframe'
import Skittle from '@/components/content/Skittle'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import Vimeo from '@/components/Vimeo'
import YouTube from '@/components/YouTube'
import { findBlog } from '@/models/blog.server'
import { pageHelper } from '@/request'
import { formatLong } from '@/utils/date'

type LoaderData = typeof loader

export async function loader({ params, url }: LoaderFunctionArgs) {
  const blog = findBlog(params.slug as string, { view: 'full' })

  if (!blog) {
    throw new Response('Not Found', { status: 404 })
  }

  const { root } = pageHelper(url)

  return {
    blog: blog,
    root: root
  }
}

export const headers: HeadersFunction = ({ parentHeaders }) => ({
  'Cache-Control': parentHeaders.get('Cache-Control') ?? ''
})

export const meta: MetaFunction<LoaderData> = ({ loaderData }) => {
  if (!loaderData) return []

  const {
    blog: { meta },
    root
  } = loaderData

  const title = `${meta.title} — PlanetScale`
  const image = `${root}${meta.image}`

  return [
    { title },
    { name: 'description', content: meta.description },
    { name: 'robots', content: meta.robots },
    { property: 'og:url', content: meta.canonical },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: meta.description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: meta.canonical },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: meta.description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: meta.canonical }
  ]
}

export default function Blog() {
  const { blog } = useLoaderData<LoaderData>()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  return (
    <>
      <SiteHeader />

      <main className='container mb-6 flex max-w-7xl flex-1 flex-col px-3 sm:px-5 lg:px-12'>
        <section
          className={cn('', {
            '-mx-5 bg-gray-100/40 px-5 lg:bg-primary! dark:bg-gray-800/50': isSidebarOpen
          })}
        >
          <p className={cn(isSidebarOpen ? 'hidden' : 'block')}>
            <Link className='pr-sm text-primary hover:text-contrast' prefetch='intent' to='/blog'>
              Blog
            </Link>

            {blog.categories.map((category) => (
              <Fragment key={category}>
                <span className='px-sm text-decoration'>|</span>
                <BlogCategoryLink category={category} className='px-sm' />
              </Fragment>
            ))}
          </p>

          <div className='flex lg:flex-row-reverse lg:gap-x-6'>
            <div className='lg:sticky lg:top-2 lg:self-start'>
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className={cn('absolute right-0 bg-gray-100 px-sm md:block lg:hidden dark:bg-gray-800', {
                  'inset-x-0 -mt-3 inline w-full text-right': isSidebarOpen,
                  '-mt-9 hidden': !isSidebarOpen
                })}
              >
                <span className={cn({ inline: !isSidebarOpen, hidden: isSidebarOpen })}>Table of contents «</span>
                <span className={cn({ hidden: !isSidebarOpen, inline: isSidebarOpen })}>Close »</span>
              </button>
              <aside
                className={cn('tree-nav w-full shrink-0 space-y-3 lg:w-36', {
                  'block py-3': isSidebarOpen,
                  'hidden lg:block': !isSidebarOpen
                })}
              >
                <div>
                  {blog.toc.length > 0 && (
                    <>
                      <h4 className='text-secondary'>Table of contents</h4>

                      <ul>
                        {blog.toc.map((item) => (
                          <li key={item.id}>
                            <Link className='font-semibold text-primary hover:text-blue' to={`#${item.id}`}>
                              {item.title}
                            </Link>

                            {item.children && item.children.length > 0 && (
                              <ul>
                                {item.children.map((child) => (
                                  <li key={child.id}>
                                    <Link className='text-primary hover:text-blue' to={`#${child.id}`}>
                                      {child.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  <div className='mt-6 mb-3 border bg-blue-50 p-3 font-semibold text-contrast dark:bg-blue-900'>
                    <p>PlanetScale, the fastest cloud Postgres, from $5/month.</p>
                    <p>
                      <a href='https://app.planetscale.com/new'>Start now</a>
                    </p>
                  </div>
                  <p>
                    Get the <a href='/blog/feed.atom'>RSS feed</a>
                  </p>
                </div>
              </aside>
            </div>

            <article
              className={cn('min-w-0 grow', {
                'hidden lg:block': isSidebarOpen
              })}
            >
              <h1>{blog.title}</h1>

              <p className='text-secondary'>
                <BlogAuthors authors={blog.authors} /> |{' '}
                <time dateTime={blog.createdAt}>{formatLong(blog.createdAt)}</time>
              </p>

              <div className='blog-post-body'>
                {renderers.react(blog.body, React, {
                  components: {
                    Callout,
                    CodeBlock,
                    ContentImage,
                    Details,
                    Iframe,
                    Skittle,
                    Vimeo,
                    YouTube
                  }
                })}
              </div>
            </article>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  )
}
